/**
 * MA-305: Appearance Override Intent Resolution
 * 
 * Resolves appearance field values with explicit user override precedence.
 * 
 * Precedence (strict):
 * 1. User explicit override (field in appearance_overrides)
 * 2. MA-305 calibrated theme default (light mode only)
 * 3. Theme token fallback
 * 4. Legacy fallback
 */

/**
 * MA-305 Calibrated Light Theme Values
 * These are the approved visual hierarchy values that apply by default.
 */
/**
 * MA-305: Cleanup stale override intent
 * 
 * Removes fields from appearance_overrides where the value equals the legacy default,
 * as these are not intentional user customizations but stale state from broken cycles.
 * 
 * @param {array} appearanceOverrides - Current override list
 * @param {object} appearance - Appearance object
 * @returns {array} Cleaned override list
 */
export function cleanupStaleOverrides(appearanceOverrides, appearance) {
  if (!Array.isArray(appearanceOverrides) || appearanceOverrides.length === 0) {
    return appearanceOverrides || [];
  }
  
  // MA-305: Legacy default that should NOT be treated as intentional override
  const LEGACY_WHITE_DEFAULT = '#ffffff';
  
  // Fields that commonly have stale white overrides from broken cycles
  const CLEANUP_CANDIDATES = ['headerBackgroundColor', 'responseAreaColor'];
  
  const cleaned = appearanceOverrides.filter(field => {
    // Keep the override if it's not a cleanup candidate
    if (!CLEANUP_CANDIDATES.includes(field)) {
      return true;
    }
    
    // Keep the override if value is NOT the legacy white default
    const value = appearance?.[field];
    if (value && value.toLowerCase() !== LEGACY_WHITE_DEFAULT.toLowerCase()) {
      return true;
    }
    
    // Remove the stale override: field is marked as override but value is legacy white
    console.log(`[MA-305-CLEANUP] Removing stale override: ${field} = ${value} (legacy default)`);
    return false;
  });
  
  if (cleaned.length !== appearanceOverrides.length) {
    console.log('[MA-305-CLEANUP] Cleaned overrides:', {
      before: appearanceOverrides,
      after: cleaned,
    });
  }
  
  return cleaned;
}

export const MA305_CALIBRATED_VALUES = {
  threadColor: '#f8fafc',
  headerBackgroundColor: '#f1f5f9',
  responseAreaColor: '#f1f5f9',
  botColor: '#e5e7eb',
  containerColor: '#f8fafc',
};

/**
 * Resolve appearance field value with override intent awareness
 * 
 * @param {string} field - Field name (e.g., 'headerBackgroundColor')
 * @param {object} appearance - Appearance object from funnel
 * @param {array} appearanceOverrides - List of explicitly customized fields
 * @param {object} ma305Defaults - MA-305 calibrated values (light mode)
 * @param {object} themeTokens - Theme token values
 * @param {*} fallback - Legacy fallback value
 * @param {boolean} isLightMode - Whether light mode is active
 * @returns {*} Resolved value
 */
export function resolveAppearanceValue(
  field,
  appearance,
  appearanceOverrides,
  ma305Defaults,
  themeTokens,
  fallback,
  isLightMode = true
) {
  // MA-305 FORENSIC TRACE
  if (field === 'headerBackgroundColor' || field === 'responseAreaColor') {
    console.log(`[MA-305-FORENSIC] Resolving ${field}:`, {
      appearanceOverrides,
      hasOverride: appearanceOverrides?.includes(field),
      appearanceValue: appearance?.[field],
      isLightMode,
      ma305Default: ma305Defaults?.[field],
      themeToken: themeTokens?.[field],
      fallback,
    });
  }
  
  // Priority 1: User explicit override
  if (appearanceOverrides?.includes(field)) {
    const result = appearance?.[field];
    if (field === 'headerBackgroundColor' || field === 'responseAreaColor') {
      console.log(`[MA-305-FORENSIC] ${field} - Priority 1 (Override):`, result);
    }
    return result;
  }
  
  // Priority 2: MA-305 calibrated theme default (light mode only)
  if (isLightMode && ma305Defaults?.[field]) {
    const result = ma305Defaults[field];
    if (field === 'headerBackgroundColor' || field === 'responseAreaColor') {
      console.log(`[MA-305-FORENSIC] ${field} - Priority 2 (MA-305 Default):`, result);
    }
    return result;
  }
  
  // Priority 3: Theme token
  if (themeTokens?.[field]) {
    const result = themeTokens[field];
    if (field === 'headerBackgroundColor' || field === 'responseAreaColor') {
      console.log(`[MA-305-FORENSIC] ${field} - Priority 3 (Theme Token):`, result);
    }
    return result;
  }
  
  // Priority 4: Legacy fallback
  const result = appearance?.[field] ?? fallback;
  if (field === 'headerBackgroundColor' || field === 'responseAreaColor') {
    console.log(`[MA-305-FORENSIC] ${field} - Priority 4 (Legacy Fallback):`, result);
  }
  return result;
}

/**
 * Add field to appearance overrides list
 * @param {string} field - Field name to add
 * @param {array} currentOverrides - Current overrides list
 * @returns {array} Updated overrides list
 */
export function addAppearanceOverride(field, currentOverrides = []) {
  if (currentOverrides.includes(field)) {
    return currentOverrides;
  }
  return [...currentOverrides, field];
}

/**
 * Remove field from appearance overrides list
 * @param {string} field - Field name to remove
 * @param {array} currentOverrides - Current overrides list
 * @returns {array} Updated overrides list
 */
export function removeAppearanceOverride(field, currentOverrides = []) {
  return currentOverrides.filter(f => f !== field);
}

/**
 * Check if field is using theme default (not overridden)
 * @param {string} field - Field name
 * @param {array} appearanceOverrides - Current overrides list
 * @returns {boolean} True if using theme default
 */
export function isUsingThemeDefault(field, appearanceOverrides = []) {
  return !appearanceOverrides.includes(field);
}
